import { useEffect, useState } from 'react';
import { extractReceiptFields } from '../services/receiptAutofillService';
import { convertImageFileToWebpDataUrl } from '../utils/imageConverter';

function useReceiptAutofill({ onAutofill, initialImageUrl = '' }) {
  const [isAutofilling, setIsAutofilling] = useState(false);
  const [autofillError, setAutofillError] = useState('');
  const [autofillMessage, setAutofillMessage] = useState('');
  const [hasAutofilled, setHasAutofilled] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState('');

  useEffect(() => {
    const normalizedInitialImageUrl = String(initialImageUrl || '').trim();

    setReceiptFile(
      normalizedInitialImageUrl
        ? {
            name: getImageFileName(normalizedInitialImageUrl),
            type: 'image/webp',
          }
        : null
    );
    setReceiptPreviewUrl(normalizedInitialImageUrl);
    setAutofillError('');
    setAutofillMessage('');
    setHasAutofilled(false);
  }, [initialImageUrl]);

  async function handleReceiptSelect(file) {
    if (!file) {
      return;
    }

    setReceiptFile(file);
    setAutofillError('');
    setAutofillMessage('');

    try {
      const convertedImage = await convertImageFileToWebpDataUrl(file);
      const webpFileName = getWebpFileName(file.name);

      setReceiptFile({
        name: webpFileName,
        type: convertedImage.mimeType,
      });
      setReceiptPreviewUrl(convertedImage.dataUrl);
    } catch (error) {
      setAutofillError(error.message);
    }
  }

  async function handleReceiptAutofill() {
    if (!receiptFile || !receiptPreviewUrl) {
      setAutofillError('Capture a receipt before reading it.');
      return;
    }

    setIsAutofilling(true);
    setAutofillError('');
    setAutofillMessage('');

    try {
      const response = await extractReceiptFields({
        fileName: receiptFile.name,
        mimeType: receiptFile.type,
        imageData: receiptPreviewUrl,
      });

      onAutofill(response.fields || {});
      setHasAutofilled(true);
      setAutofillMessage('Receipt details were auto-filled. Please review before submitting.');
    } catch (error) {
      setAutofillError(error.message);
    } finally {
      setIsAutofilling(false);
    }
  }

  function clearReceiptCapture() {
    setReceiptFile(null);
    setReceiptPreviewUrl('');
    setAutofillError('');
    setAutofillMessage('');
    setHasAutofilled(false);
  }

  return {
    isAutofilling,
    autofillError,
    autofillMessage,
    hasAutofilled,
    receiptPreviewUrl,
    handleReceiptSelect,
    handleReceiptAutofill,
    clearReceiptCapture,
  };
}

function getWebpFileName(fileName) {
  const cleanName = String(fileName || 'receipt').trim() || 'receipt';
  const dotIndex = cleanName.lastIndexOf('.');
  const baseName = dotIndex > 0 ? cleanName.slice(0, dotIndex) : cleanName;

  return `${baseName}.webp`;
}

function getImageFileName(imageUrl) {
  const cleanUrl = String(imageUrl || '').split('?')[0];
  const fileName = cleanUrl.split('/').filter(Boolean).pop() || 'receipt.webp';

  return fileName.includes('.') ? fileName : `${fileName}.webp`;
}

export default useReceiptAutofill;
